import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  type ClassSession,
  useGetClassSessionsAttendanceAnalytics,
} from '~entities/classes/api/class-sessions-attendance-analytics';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Badge } from '~shared/shadcn/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

const columnHelper = createColumnHelper<ClassSession>();

const columns = [
  columnHelper.accessor('date', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Date'
          className='min-w-12'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('startTime', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Time'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('room', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Room'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('instructor', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Instructor'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('status', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Status'
        />
      );
    },
    cell: (info) => {
      return <Badge>{info.getValue()}</Badge>;
    },
  }),
];

export interface SessionsTabProps {
  classId: string;
}

export const SessionsTab = ({ classId }: SessionsTabProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isLoading } = useGetClassSessionsAttendanceAnalytics({
    enrollmentId: classId,
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
  });

  if (isLoading)
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2 className='animate-spin' />
      </div>
    );

  if (!data) {
    return <div>No data available</div>;
  }

  const studentPerformance = data.data.classSessions;
  const total = data.data.pagination.totalItems;

  return (
    <Card>
      <CardHeader className='p-2 sm:p-6'>
        <CardTitle>Class Sessions</CardTitle>
      </CardHeader>
      <CardContent className='p-2 sm:p-6'>
        <EnrollioTable
          ariaLabel='Class sessions'
          totalLabel='Class Sessions'
          columns={columns}
          data={studentPerformance}
          isUpdating={isLoading}
          rowCount={total}
          pagination={pagination}
          onPaginationChange={setPagination}
          manualPagination
        />
      </CardContent>
    </Card>
  );
};
